<template>
  <div class="shape-arrange">
    <LevelComplete v-if="isGameComplete" />
    <div v-if="!isGameComplete">
      <div class="targets">
        <div
          v-for="(shape, index) in currentCategory"
          :key="index"
          class="target-shape"
          :class="shape.toLowerCase() + '-target'"
          :style="getTargetShapeStyle(shape)"
          @dragover.prevent
          @drop="handleDrop($event, shape)"
          @click="speakOutLoud(shape)"
        >
          <!-- No label inside target shapes -->
        </div>
      </div>
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
      <div class="random-grid">
        <div
          v-for="(item, index) in shapes"
          :key="index"
          class="shape-draggable"
          :class="item.shape.toLowerCase()"
          :style="getShapeStyle(item)"
          draggable="true"
          @dragstart="handleDrag($event, item.shape)"
          @click="speakOutLoud(item.shape)"
          @touchstart="handleTouchStart($event, item)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          <!-- Only draggable shapes have labels -->
          <span class="shape-label">{{ item.shape }}</span>
        </div>
        <!-- For touch dragging -->
        <div
          v-if="draggedShape && isTouchDevice"
          :class="['shape-draggable', draggedShape.toLowerCase()]"
          :style="draggedStyle"
        >
          <span class="shape-label">{{ draggedShape }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  props: {
    selectedCategory: {
      type: String,
      default: "basicShapes",
    },
    difficulty: {
      type: Number,
      default: 1,
      validator: (value) => [1, 2, 3].includes(value),
    },
  },
  data() {
    return {
      categories: {
        basicShapes: ["Circle", "Square", "Triangle", "Rectangle", "Pentagon"],
      },
      shapes: [],
      shapeColors: {
        Circle: "#FF5733",
        Square: "#33FF57",
        Triangle: "#3357FF",
        Rectangle: "#FF33A1",
        Pentagon: "#A133FF",
      },
      message: "",
      isCorrectDrop: {},
      shapeCounts: {},
      maxCount: 1,
      selectedVoice: null,
      isGameComplete: false,
      touchStartX: 0,
      touchStartY: 0,
      touchOffsetX: 0,
      touchOffsetY: 0,
      containerOffsetX: 0,
      containerOffsetY: 0,
      draggedShape: null,
      draggedItem: null,
      draggedStyle: {},
      isTouchDevice: false,
    };
  },
  computed: {
    currentCategory() {
      return this.categories[this.selectedCategory];
    },
  },
  mounted() {
    this.initializeShapeCounts();
    this.setupGame();
    // Initialize isCorrectDrop with each shape
    this.currentCategory.forEach((shape) => {
      this.isCorrectDrop[shape] = { dropped: false, color: "" };
    });
    window.speechSynthesis.onvoiceschanged = this.setupVoices;
    this.setupVoices();
    this.isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
  },
  methods: {
    initializeShapeCounts() {
      this.shapeCounts = this.currentCategory.reduce((acc, shape) => {
        if (!acc[shape]) acc[shape] = 0;
        return acc;
      }, {});
    },
    setupGame() {
      const availableShapes = this.currentCategory.filter(
        (shape) => this.shapeCounts[shape] < this.maxCount
      );

      if (availableShapes.length === 0 && this.isGameComplete !== true) {
        this.isGameComplete = true;
        this.speakOutLoud('Great Job!');
        setTimeout(() => {
          this.$router.push("/shapes");
        }, 4000);
        return;
      }

      this.shapes = availableShapes.map((shape) => {
        const randomX = Math.floor(Math.random() * 80);
        const randomY = Math.floor(Math.random() * 80);
        return {
          shape,
          position: {
            left: `${randomX}%`,
            top: `${randomY}%`,
          },
        };
      });

      // Reset isCorrectDrop for each shape
      this.currentCategory.forEach((shape) => {
        this.isCorrectDrop[shape] = { dropped: false, color: "" };
      });

      this.message = "";
    },
    handleDrag(event, shape) {
      event.dataTransfer.setData("text/plain", shape);
      // Let the browser handle the drag image to retain shape appearance
    },
    handleDrop(event, targetShape) {
      const droppedShape = event.dataTransfer.getData("text/plain");
      if (droppedShape === targetShape) {
        this.playSound(
          "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
        );
        this.speakOutLoud(`${targetShape}`);
        // Update isCorrectDrop with drop status and color
        this.isCorrectDrop[targetShape] = {
          dropped: true,
          color: this.shapeColors[droppedShape],
        };
        this.shapeCounts[targetShape]++;
        // Remove the shape from draggable shapes
        this.shapes = this.shapes.filter((item) => item.shape !== droppedShape);
        setTimeout(() => {
          if (
            Object.values(this.isCorrectDrop).every((status) => status.dropped)
          ) {
            this.resetGame();
          }
        }, 3000);
      } else {
        this.message = "Try again!";
        setTimeout(() => {
          this.message = "";
        }, 3000);
      }
    },
    handleTouchStart(event, item) {
      event.preventDefault();
      const touch = event.touches[0];

      const rect = event.target.getBoundingClientRect();
      const containerRect = this.$el
        .querySelector(".random-grid")
        .getBoundingClientRect();

      this.touchOffsetX = touch.clientX - rect.left;
      this.touchOffsetY = touch.clientY - rect.top;

      this.containerOffsetX = containerRect.left;
      this.containerOffsetY = containerRect.top;

      this.draggedShape = item.shape;
      this.draggedItem = item;

      this.draggedStyle = {
        position: "absolute",
        left: `${rect.left - containerRect.left}px`,
        top: `${rect.top - containerRect.top}px`,
        zIndex: 1000,
        width: "100px",
        height: "100px",
        backgroundColor: this.shapeColors[this.draggedShape],
        clipPath: this.getClipPath(this.draggedShape),
        borderLeft: "",
        borderRight: "",
        borderBottom: "",
      };

      // Adjust for specific shapes like Triangle and Rectangle
      if (this.draggedShape === "Triangle") {
        this.draggedStyle.width = "0";
        this.draggedStyle.height = "0";
        this.draggedStyle.backgroundColor = "transparent";
        this.draggedStyle.borderLeft = "50px solid transparent";
        this.draggedStyle.borderRight = "50px solid transparent";
        this.draggedStyle.borderBottom = `100px solid ${
          this.shapeColors[this.draggedShape]
        }`;
      } else if (this.draggedShape === "Rectangle") {
        this.draggedStyle.width = "150px";
      }
    },
    handleTouchMove(event) {
      event.preventDefault();
      const touch = event.touches[0];

      const relativeX =
        touch.clientX - this.containerOffsetX - this.touchOffsetX;
      const relativeY =
        touch.clientY - this.containerOffsetY - this.touchOffsetY;

      this.draggedStyle.left = `${relativeX}px`;
      this.draggedStyle.top = `${relativeY}px`;
    },
    handleTouchEnd(event) {
      const dropZones = this.$el.querySelectorAll(".target-shape");
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;

      dropZones.forEach((dropZone, index) => {
        const rect = dropZone.getBoundingClientRect();
        if (
          touchEndX >= rect.left &&
          touchEndX <= rect.right &&
          touchEndY >= rect.top &&
          touchEndY <= rect.bottom
        ) {
          this.handleDrop(
            {
              dataTransfer: { getData: () => this.draggedShape },
            },
            this.currentCategory[index]
          );
        }
      });

      this.draggedShape = null;
      this.draggedItem = null;
      this.draggedStyle = {};
    },
    getClipPath(shape) {
      if (shape === "Pentagon") {
        return "polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%)";
      }
      // Add more shapes if necessary
      return null;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    resetGame() {
      this.setupGame();
    },
    getTargetShapeStyle(shape) {
      const dropped = this.isCorrectDrop[shape]?.dropped;
      const color = this.isCorrectDrop[shape]?.color || "";
      let style = {};

      if (shape === "Circle" || shape === "Square" || shape === "Rectangle") {
        style.backgroundColor = dropped ? color : "#808080";
      }
      if (shape === "Rectangle") {
        style.width = "150px";
      }
      if (shape === "Triangle") {
        style.borderLeft = "50px solid transparent";
        style.borderRight = "50px solid transparent";
        style.borderBottom = dropped
          ? `100px solid ${color}`
          : "100px solid #808080";
        style.width = "0";
        style.height = "0";
      }
      if (shape === "Pentagon") {
        style.backgroundColor = dropped ? color : "#808080";
        style.clipPath =
          "polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%)";
      }
      return style;
    },
    getShapeStyle(item) {
      let style = {
        ...item.position,
        position: "absolute",
      };

      const color = this.shapeColors[item.shape];

      if (
        this.draggedItem &&
        item.shape === this.draggedItem.shape &&
        this.isTouchDevice
      ) {
        // Hide the original shape while dragging on touch devices
        style.display = "none";
      } else {
        if (item.shape === "Triangle") {
          style.width = "0";
          style.height = "0";
          style.borderLeft = "50px solid transparent";
          style.borderRight = "50px solid transparent";
          style.borderBottom = `100px solid ${color}`;
        } else if (item.shape === "Rectangle") {
          style.backgroundColor = color;
          style.width = "150px";
        } else if (item.shape === "Pentagon") {
          style.backgroundColor = color;
          style.clipPath =
            "polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%)";
        } else if (item.shape === "Circle" || item.shape === "Square") {
          style.backgroundColor = color;
        }
      }

      return style;
    },
  },
};
</script>

<style scoped>
.targets {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.target-shape {
  width: 100px;
  height: 100px;
  margin: 10px;
  position: relative;
}

.shape-draggable {
  width: 100px;
  height: 100px;
  cursor: grab;
  padding: 0;
  transition: transform 0.3s;
  position: absolute;
}

.shape-draggable .shape-label {
  position: absolute;
  width: 100%;
  text-align: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1rem;
  font-weight: bold;
  color: #ffffff;
}

.shape-draggable:hover {
  transform: scale(1.1);
}

/* Circle */
.circle-target {
  background-color: #808080;
  border-radius: 50%;
}

.circle {
  border-radius: 50%;
}

/* Square */
.square-target {
  background-color: #808080;
}

.square {
  /* No background-color here */
}

/* Rectangle */
.rectangle-target {
  background-color: #808080;
  width: 150px;
}

.rectangle {
  width: 150px;
  /* No background-color here */
}

/* Triangle */
.triangle-target {
  width: 0;
  height: 0;
  border-left: 50px solid transparent;
  border-right: 50px solid transparent;
  border-bottom: 100px solid #808080;
}

.triangle {
  width: 0;
  height: 0;
  /* Borders will be set dynamically */
}

.triangle .shape-label,
.triangle-target .shape-label {
  top: 70%;
}

/* Pentagon */
.pentagon-target {
  width: 100px;
  height: 100px;
  background-color: #808080;
  clip-path: polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%);
  position: relative;
}

.pentagon {
  width: 100px;
  height: 100px;
  clip-path: polygon(50% 0%, 100% 38%, 81% 100%, 19% 100%, 0% 38%);
  position: relative;
  /* No background-color here */
}

.pentagon .shape-label,
.pentagon-target .shape-label {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #ffffff;
}

/* For better touch experience */
@media (hover: none) {
  .shape-draggable:hover {
    transform: none;
  }
}

.random-grid {
  position: relative;
  width: 100%;
  height: 300px;
  margin-bottom: 30px;
}

.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}
</style>
